import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { DataManager } from '@syncfusion/ej2-data';
import { Observable } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';
import { ODataAdaptor } from '../../adaptors/odata-adaptor';
import { UploadUtilService } from '../../common/photo-uploader/upload-util.service';
import { ModeleTheme } from '../../constantes/publipostage/modele-theme';
import { MediaPublipostage } from '../../constantes/publipostage/type-modele';
import {
  ListesParametragesModeleMailViewModel,
  ParametrageModeleMailModel,
  ParametrageModeleMailViewModel,
} from '../../models/publipostage-modele/listesParametragesModeleMailViewModel';
import { PublipostageModeleView } from '../../models/publipostage-modele/publipostage-modele';
import { PublipostageModeleCourrier } from '../../models/publipostage-modele/publipostage-modele-courrier';
import { PublipostageModeleMail } from '../../models/publipostage-modele/publipostage-modele-mail';
import { ToastErrorModel } from '../../models/toasts/toast-error.model';
import { ToastSuccesModel } from '../../models/toasts/toast-succes.model';
import { FiltreOperations } from '../../pages/publipostage-modeles/panel-champs-fusion/constantes/filtre-champs-fusion';
import { IChampFusion } from '../../pages/publipostage-modeles/panel-champs-fusion/model/ichamp-fusion';
import { ModeleCourrierDetailleViewModel } from '../../resolvers/modele-courrier-resolver/modele-courrier-resolver.service';
import { ConfigService } from '../config/config.service';
import { NotificationToastService } from '../notifications/notification-toast.service';
import { LocalStorageTokenService } from '../utilisateurs/local-storage-token.service';

@Injectable({
  providedIn: 'root',
})
export class PublipostageModelesService {
  private readonly baseUrlApi = this.configService.config.urlApi;;

  constructor(
    private oDataAdaptor: ODataAdaptor,
    private client: HttpClient,
    private notificationToastService: NotificationToastService,
    private uploadUtilService: UploadUtilService,
    private configService: ConfigService,
    private localStorageTokenService: LocalStorageTokenService,
  ) {}

  buildDataManagerGetModeles(): DataManager {
    return new DataManager({
      url: `${this.baseUrlApi}/api/p/ModelesView`,
      adaptor: this.oDataAdaptor,
    });
  }

  getModeleMailDetaille(idPublipostageModele: number): Observable<PublipostageModeleMail> {
    return this.client.get<PublipostageModeleMail>(
      `${this.baseUrlApi}/api/p/ModelesMail/GetModeleMail/` + idPublipostageModele,
    );
  }

  addModeleMail(modele: PublipostageModeleMail): Observable<any> {
    return this.client
      .put<number>(`${this.baseUrlApi}/api/p/ModelesMail/AddModeleMail`, modele)
      .pipe(tap(() => this.notificationToastService.push(new ToastSuccesModel('Le modèle a bien été créé.'))));
  }

  updateModeleMail(modele: PublipostageModeleMail): Observable<any> {
    return this.client
      .post<number>(`${this.baseUrlApi}/api/p/ModelesMail/UpdateModeleMail`, modele)
      .pipe(tap(() => this.notificationToastService.push(new ToastSuccesModel('Le modèle a bien été modifié.'))));
  }

  deleteModeleMail(modeleId: number) {
    return this.client.delete(`${this.baseUrlApi}/api/p/ModelesMail/DeleteModeleMail/${modeleId}`).pipe(
      tap(() => {
        this.notificationToastService.push(new ToastSuccesModel(`Le modèle de publipostage a bien été supprimé.`));
      }),
    );
  }

  addModeleCourrier(modele: PublipostageModeleCourrier): Observable<number> {
    return this.client
      .put<number>(`${this.baseUrlApi}/api/p/ModelesCourrier/Ajouter`, modele)
      .pipe(tap(() => this.notificationToastService.push(new ToastSuccesModel(`Le modèle a bien été créé`))));
  }

  getModeleCourrierDetaille(id: number): Observable<ModeleCourrierDetailleViewModel> {
    return this.client.get<ModeleCourrierDetailleViewModel>(`${this.baseUrlApi}/api/p/ModelesCourrier/Get/${id}`);
  }

  updateModeleCourrier(saveRecord: PublipostageModeleCourrier): Observable<void> {
    return this.client
      .post<void>(`${this.baseUrlApi}/api/p/ModelesCourrier/Modifier`, saveRecord)
      .pipe(tap(() => this.notificationToastService.push(new ToastSuccesModel(`Le modèle a bien été modifié`))));
  }

  deleteModeleCourrier(Id: number): Observable<void> {
    return this.client
      .delete<void>(`${this.baseUrlApi}/api/p/ModelesCourrier/Supprimer/${Id}`)
      .pipe(tap(() => this.notificationToastService.push(new ToastSuccesModel(`Le modèle a bien été supprimé`))));
  }

  loadChampsPublipostage(theme: ModeleTheme, text: string, motsCles: Array<string>, operation: FiltreOperations): Observable<IChampFusion[]> {
    let path = `api/p/ChampsPublipostage/${theme.valueOf()}?`

    const searchText = text ? `contains(tolower(cast(Code, 'Edm.String')), '${text}') or contains(tolower(cast(Description, 'Edm.String')), '${text}')`: null;
    const filtreResult = motsCles?.length > 0 ? this.getFiltreChampsFusionFormated(motsCles, operation) : null;

    if(searchText && filtreResult) {
      path += `$filter=(${searchText}) and (${filtreResult})`;
    }
    else if(filtreResult)
    {
      path += `$filter=${filtreResult}`;
    }
    else if(searchText){
      path += `$filter=${searchText}`;
    }

    return this.client.get<IChampFusion[]>(`${this.baseUrlApi}/${path}`);
  }

  getModelesByTheme(typeModele: MediaPublipostage, theme: ModeleTheme, idEntite?: number): Observable<PublipostageModeleView[]> {
    const entiteTravail = idEntite ? idEntite: '';
    if (theme == null) {
      throw new Error('Thème indéfini !');
    }
    const num: number = theme.valueOf();
    if (typeModele == MediaPublipostage.Email) {
      return this.client.get<Array<PublipostageModeleView>>(
        `${this.baseUrlApi}/api/p/ModelesMail/GetModelesMail/${num.toString()}?idEntite=${entiteTravail}`,
      );
    } else if (typeModele == MediaPublipostage.CourrierASigner){
      return this.client.get<Array<PublipostageModeleView>>(
        `${this.baseUrlApi}/api/p/ModelesCourrier/GetModelesCourrier/${num.toString()}?modelesASigner=true`,
      );
    } else {
      return this.client.get<Array<PublipostageModeleView>>(
        `${this.baseUrlApi}/api/p/ModelesCourrier/GetModelesCourrier/${num.toString()}?modelesASigner=false`,
      );
    }
  }

  getModelesCourrierByThemeAndEntiteId(theme: ModeleTheme, idEntite: number) {
    const num: number = theme.valueOf();
    return this.client.get<Array<PublipostageModeleView>>(
      `${this.baseUrlApi}/api/p/ModelesCourrier/GetModelesCourrierByEntite?idTheme=${num}&idEntite=${idEntite}`,
    );
  }

  getFiltreChampsFusionFormated(filtre: Array<string>, operation: FiltreOperations) : string{
    let formatedFiltre = '';
    if(operation == FiltreOperations.startstWith)
    {
      for(const key of filtre ){
        formatedFiltre += `startswith(tolower(Code),'${key.toLowerCase()}') or `;
      }
      return formatedFiltre.substring(0, formatedFiltre.length - 3);
    }

    if(operation == FiltreOperations.notStartsWith){
      for(const key of filtre ){
        formatedFiltre += `not startswith(tolower(Code),'${key.toLowerCase()}') and `;
      }
      return formatedFiltre.substring(0, formatedFiltre.length - 4);
    }
  }

  getSfdtContentByAnyFile(file: File): Observable<string> {
    return this.uploadUtilService
      .uploadFile(`${this.baseUrlApi}/api/p/Modeles/GetSfdtContentByAnyFile/`, {File: file})
      .pipe(
        map((result: string) => {
          if (result == null || result === '') {
            this.notificationToastService.push(new ToastErrorModel("Impossible de charger le fichier"));
            return null;
          }

          return result;
        }),
        catchError(async (err:HttpErrorResponse) => {
          this.notificationToastService.push(new ToastErrorModel(err?.error?.title ?? err.statusText));
          return null;
        })
      );
  }

  getListesParametrageModeleMail(): Observable<ListesParametragesModeleMailViewModel>
  {
    return this.client.get<ListesParametragesModeleMailViewModel>(`${this.baseUrlApi}/api/w/ModeleMail/GetListesParametres`);
  }

  getParametrageModeleMail(idModele: number): Observable<ParametrageModeleMailViewModel>
  {
    return this.client.get<ParametrageModeleMailViewModel>(`${this.baseUrlApi}/api/w/ModeleMail/GetParametrage/${idModele}`);
  }

  updateParametrageModele(parametrage: ParametrageModeleMailModel): Observable<any> {
    return this.client.post<void>(`${this.baseUrlApi}/api/w/ModeleMail/UpdateParametrage`, parametrage);
  }
}
